import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Navigation, X, Home, Briefcase, Star, Clock, ChevronRight, Search, Plus, Trash2 } from 'lucide-react';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// localStorage keys (fallback for guests)
const SAVED_PLACES_KEY = 'fq_saved_places';
const RECENT_PLACES_KEY = 'fq_recent_places';
const LOCATION_SET_KEY = 'fq_location_set_permanently';

const UberStyleLocationPicker = ({ onClose }) => {
  const { requestGPS, setGeocodedLocation } = useLocation();
  const { user } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');
  const [savedPlaces, setSavedPlaces] = useState([]);
  const [recentPlaces, setRecentPlaces] = useState([]);
  const [showAddFavorite, setShowAddFavorite] = useState(false);
  const [selectedForSave, setSelectedForSave] = useState(null);
  const [newPlaceLabel, setNewPlaceLabel] = useState('');

  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // ── Data fetching helpers ──────────────────────────────────────────────

  const fetchSavedPlaces = useCallback(async () => {
    if (!user) {
      setSavedPlaces(JSON.parse(localStorage.getItem(SAVED_PLACES_KEY) || '[]'));
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/location/saved-places`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSavedPlaces(data.places || []);
      }
    } catch {
      setSavedPlaces(JSON.parse(localStorage.getItem(SAVED_PLACES_KEY) || '[]'));
    }
  }, [user]);

  const fetchRecentPlaces = useCallback(async () => {
    if (!user) {
      setRecentPlaces(JSON.parse(localStorage.getItem(RECENT_PLACES_KEY) || '[]'));
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/location/recent-places`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRecentPlaces(data.places || []);
      }
    } catch {
      setRecentPlaces(JSON.parse(localStorage.getItem(RECENT_PLACES_KEY) || '[]'));
    }
  }, [user]);

  useEffect(() => {
    fetchSavedPlaces();
    fetchRecentPlaces();
  }, [fetchSavedPlaces, fetchRecentPlaces]);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // ── Search with debounce ────────────────────────────────────────────────

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchQuery.length < 2) { setSearchResults([]); return; }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await fetch(`${API_URL}/api/location/autocomplete?query=${encodeURIComponent(searchQuery)}`);
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.predictions || []);
        }
      } catch { /* ignore */ }
      finally { setIsSearching(false); }
    }, 300);

    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleUseCurrentLocation = async () => {
    setGpsLoading(true);
    const success = await requestGPS();
    setGpsLoading(false);
    if (success) {
      localStorage.setItem(LOCATION_SET_KEY, 'true');
      onClose?.();
    } else {
      setGpsError('Could not detect location. Please search manually or check your browser location permissions.');
    }
  };

  const resolvePlace = async (place) => {
    if (place.lat) return place;
    if (!place.place_id) return place;
    try {
      const res = await fetch(`${API_URL}/api/location/details?place_id=${place.place_id}`);
      if (res.ok) {
        const data = await res.json();
        return {
          ...place,
          lat: data.lat,
          lng: data.lng,
          displayName: data.formatted_address || place.description,
          city: data.city || place.structured_formatting?.main_text,
        };
      }
    } catch { /* ignore */ }
    return place;
  };

  const handleSelectPlace = async (place) => {
    const resolved = await resolvePlace(place);

    setGeocodedLocation({
      lat: resolved.lat,
      lng: resolved.lng,
      displayName: resolved.displayName || resolved.display_name || resolved.description,
      city: resolved.city || resolved.structured_formatting?.main_text,
    });

    // Track recent
    await addRecentPlace(resolved);
    localStorage.setItem(LOCATION_SET_KEY, 'true');
    onClose?.();
  };

  const addRecentPlace = async (place) => {
    const payload = {
      type: 'recent',
      display_name: place.displayName || place.display_name || place.description || '',
      city: place.city || place.structured_formatting?.main_text || '',
      place_id: place.place_id || '',
      lat: place.lat,
      lng: place.lng,
      description: place.description || place.displayName || place.display_name || '',
    };

    if (user) {
      try {
        await fetch(`${API_URL}/api/location/recent-places`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });
      } catch { /* fallback to localStorage */ }
    }
    // Always update localStorage too (for quick access)
    const recent = JSON.parse(localStorage.getItem(RECENT_PLACES_KEY) || '[]');
    const filtered = recent.filter(p => p.place_id !== place.place_id);
    const updated = [{ ...place, ...payload }, ...filtered].slice(0, 5);
    localStorage.setItem(RECENT_PLACES_KEY, JSON.stringify(updated));
  };

  const savePlaceAs = async (place, type, customLabel = '') => {
    const resolved = await resolvePlace(place);
    const label = customLabel || (type === 'home' ? 'Home' : type === 'work' ? 'Work' : customLabel);

    const payload = {
      type,
      label,
      display_name: resolved.displayName || resolved.display_name || resolved.description || '',
      city: resolved.city || resolved.structured_formatting?.main_text || '',
      place_id: resolved.place_id || '',
      lat: resolved.lat,
      lng: resolved.lng,
      description: resolved.description || resolved.displayName || resolved.display_name || '',
    };

    if (user) {
      try {
        const res = await fetch(`${API_URL}/api/location/saved-places`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          await fetchSavedPlaces();
        }
      } catch { /* fallback */ }
    }

    // Also update localStorage
    const saved = JSON.parse(localStorage.getItem(SAVED_PLACES_KEY) || '[]');
    let updated = (type === 'home' || type === 'work')
      ? saved.filter(p => p.type !== type)
      : saved;
    updated.push({ ...payload, savedAt: new Date().toISOString() });
    localStorage.setItem(SAVED_PLACES_KEY, JSON.stringify(updated));
    if (!user) setSavedPlaces(updated);

    setShowAddFavorite(false);
    setSelectedForSave(null);
    setNewPlaceLabel('');
  };

  const deleteSavedPlace = async (place) => {
    if (user && place.place_record_id) {
      try {
        await fetch(`${API_URL}/api/location/saved-places/${place.place_record_id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        await fetchSavedPlaces();
        return;
      } catch { /* fallback */ }
    }
    // localStorage fallback
    const saved = JSON.parse(localStorage.getItem(SAVED_PLACES_KEY) || '[]');
    const updated = saved.filter(p => !(p.type === place.type && p.place_id === place.place_id));
    localStorage.setItem(SAVED_PLACES_KEY, JSON.stringify(updated));
    setSavedPlaces(updated);
  };

  const clearRecentPlaces = async () => {
    if (user) {
      try {
        await fetch(`${API_URL}/api/location/recent-places`, { method: 'DELETE', credentials: 'include' });
      } catch { /* ignore */ }
    }
    localStorage.removeItem(RECENT_PLACES_KEY);
    setRecentPlaces([]);
  };

  // ── Derived data ────────────────────────────────────────────────────────

  const homePlace = savedPlaces.find(p => p.type === 'home');
  const workPlace = savedPlaces.find(p => p.type === 'work');
  const favoritesList = savedPlaces.filter(p => p.type !== 'home' && p.type !== 'work');

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white rounded-t-3xl md:rounded-3xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900" data-testid="location-picker-title">Where to?</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="location-picker-close">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for a place or address"
              className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/50"
              data-testid="location-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">

          {/* Search Results */}
          {searchQuery.length >= 2 && (
            <div className="p-2">
              {isSearching ? (
                <div className="py-8 text-center text-gray-500 text-sm">Searching...</div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-1">
                  {searchResults.map((result, idx) => (
                    <button
                      key={result.place_id || idx}
                      onClick={() => handleSelectPlace(result)}
                      className="w-full flex items-start gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors text-left"
                      data-testid={`search-result-${idx}`}
                    >
                      <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {result.structured_formatting?.main_text || result.description}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {result.structured_formatting?.secondary_text || ''}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedForSave(result); setShowAddFavorite(true); }}
                        className="p-1.5 hover:bg-gray-200 rounded-full flex-shrink-0"
                        title="Save this place"
                        data-testid={`save-place-btn-${idx}`}
                      >
                        <Star className="w-4 h-4 text-gray-400" />
                      </button>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500 text-sm">No results found</div>
              )}
            </div>
          )}

          {/* Default View */}
          {searchQuery.length < 2 && (
            <div className="p-4 space-y-6">

              {/* Current Location */}
              <button
                onClick={handleUseCurrentLocation}
                disabled={gpsLoading}
                className="w-full flex items-center gap-3 p-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors"
                data-testid="use-current-location-btn"
              >
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Navigation className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-sm">{gpsLoading ? 'Detecting location...' : 'Use current location'}</p>
                  <p className="text-xs text-gray-400">{gpsLoading ? 'This may take a few seconds' : 'Using GPS or network location'}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
              {gpsError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-testid="gps-error">
                  {gpsError}
                </div>
              )}

              {/* Saved Places */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3" data-testid="saved-places-heading">Saved Places</h3>
                <div className="space-y-2">
                  {/* Home */}
                  {homePlace ? (
                    <SavedPlaceRow
                      icon={<Home className="w-5 h-5" />}
                      iconBg="bg-blue-100 text-blue-600"
                      label="Home"
                      subtitle={homePlace.display_name || homePlace.displayName || homePlace.description}
                      onSelect={() => handleSelectPlace(homePlace)}
                      onDelete={() => deleteSavedPlace(homePlace)}
                    />
                  ) : (
                    <AddPlaceRow
                      icon={<Home className="w-5 h-5 text-gray-400" />}
                      label="Add Home"
                      subtitle="Save your home address"
                      testId="add-home-btn"
                      onTap={() => { searchInputRef.current?.focus(); }}
                    />
                  )}

                  {/* Work */}
                  {workPlace ? (
                    <SavedPlaceRow
                      icon={<Briefcase className="w-5 h-5" />}
                      iconBg="bg-purple-100 text-purple-600"
                      label="Work"
                      subtitle={workPlace.display_name || workPlace.displayName || workPlace.description}
                      onSelect={() => handleSelectPlace(workPlace)}
                      onDelete={() => deleteSavedPlace(workPlace)}
                    />
                  ) : (
                    <AddPlaceRow
                      icon={<Briefcase className="w-5 h-5 text-gray-400" />}
                      label="Add Work"
                      subtitle="Save your work address"
                      testId="add-work-btn"
                      onTap={() => { searchInputRef.current?.focus(); }}
                    />
                  )}

                  {/* Favorites */}
                  {favoritesList.map((place, idx) => (
                    <SavedPlaceRow
                      key={place.place_record_id || idx}
                      icon={<Star className="w-5 h-5" />}
                      iconBg="bg-amber-100 text-amber-600"
                      label={place.label}
                      subtitle={place.display_name || place.displayName || place.description}
                      onSelect={() => handleSelectPlace(place)}
                      onDelete={() => deleteSavedPlace(place)}
                    />
                  ))}
                </div>
              </div>

              {/* Recent Places */}
              {recentPlaces.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900" data-testid="recent-places-heading">Recent</h3>
                    <button onClick={clearRecentPlaces} className="text-xs text-gray-500 hover:text-[#FF6B35]" data-testid="clear-recent-btn">Clear all</button>
                  </div>
                  <div className="space-y-1">
                    {recentPlaces.map((place, idx) => (
                      <button
                        key={place.recent_id || idx}
                        onClick={() => handleSelectPlace(place)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
                        data-testid={`recent-place-${idx}`}
                      >
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5 text-gray-400" />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">
                            {place.city || place.structured_formatting?.main_text || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {place.display_name || place.displayName || place.description}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save Place Modal Overlay */}
        {showAddFavorite && selectedForSave && (
          <div className="absolute inset-0 bg-white z-10 flex flex-col">
            <div className="px-4 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">Save Place</h2>
                <button onClick={() => { setShowAddFavorite(false); setSelectedForSave(null); }} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1 truncate">
                {selectedForSave.structured_formatting?.main_text || selectedForSave.description}
              </p>
            </div>

            <div className="p-4 space-y-3">
              <button
                onClick={() => savePlaceAs(selectedForSave, 'home')}
                className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
                data-testid="save-as-home-btn"
              >
                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center"><Home className="w-5 h-5" /></div>
                <span className="font-medium text-gray-900">Save as Home</span>
              </button>

              <button
                onClick={() => savePlaceAs(selectedForSave, 'work')}
                className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-colors"
                data-testid="save-as-work-btn"
              >
                <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center"><Briefcase className="w-5 h-5" /></div>
                <span className="font-medium text-gray-900">Save as Work</span>
              </button>

              <div className="pt-2">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Or add custom label</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPlaceLabel}
                    onChange={(e) => setNewPlaceLabel(e.target.value)}
                    placeholder="e.g., Gym, Mom's house..."
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/50"
                    data-testid="custom-label-input"
                  />
                  <button
                    onClick={() => newPlaceLabel && savePlaceAs(selectedForSave, 'favorite', newPlaceLabel)}
                    disabled={!newPlaceLabel}
                    className="px-4 py-3 bg-[#FF6B35] text-white rounded-xl font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="save-custom-label-btn"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────

const SavedPlaceRow = ({ icon, iconBg, label, subtitle, onSelect, onDelete }) => (
  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl group">
    <div className={`w-10 h-10 ${iconBg} rounded-full flex items-center justify-center`}>{icon}</div>
    <button onClick={onSelect} className="flex-1 text-left min-w-0">
      <p className="font-medium text-sm text-gray-900">{label}</p>
      <p className="text-xs text-gray-500 truncate">{subtitle}</p>
    </button>
    <button onClick={onDelete} className="p-1.5 hover:bg-gray-200 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-${label?.toLowerCase()}-btn`}>
      <Trash2 className="w-4 h-4 text-gray-400" />
    </button>
  </div>
);

const AddPlaceRow = ({ icon, label, subtitle, testId, onTap }) => (
  <button
    onClick={onTap}
    className="w-full flex items-center gap-3 p-3 border border-dashed border-gray-300 rounded-xl hover:border-[#FF6B35] hover:bg-[#FFF5F0] transition-colors"
    data-testid={testId}
  >
    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">{icon}</div>
    <div className="flex-1 text-left">
      <p className="font-medium text-sm text-gray-600">{label}</p>
      <p className="text-xs text-gray-400">{subtitle}</p>
    </div>
    <Plus className="w-5 h-5 text-gray-400" />
  </button>
);

export default UberStyleLocationPicker;
